import React from "react";
import { Box, Image, Flex, Heading, Text, VStack } from "@chakra-ui/react";
import { TEXTS } from "../constants/texts";
import ScheduleImage from "../images/Menu/Schedule.png";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";

interface CommonImageProps {
  onContextMenu: (e: { preventDefault: () => void }) => void;
  draggable: boolean;
}

const preventDefault = (e: { preventDefault: () => void }): void => {
  e.preventDefault();
};

const commonImageProps: CommonImageProps = {
  onContextMenu: preventDefault,
  draggable: false,
};

interface ScheduleHeaderProps {
  scrollDirection: "up" | "down";
}

const ScheduleHeader: React.FC<ScheduleHeaderProps> = ({ scrollDirection }) => {
  // Intersection Observer設定
  const { ref: storyImageRef, inView: storyImageInView } = useInView({
    threshold: 0.3,
    triggerOnce: false,
  });

  const { ref: headingRef, inView: headingInView } = useInView({
    threshold: 0.3,
    triggerOnce: false,
  });

  // アニメーション状態の決定
  const getAnimationState = (isInView: boolean) => {
    if (!isInView) return "hidden";
    return scrollDirection === "up" ? "instant" : "visible";
  };

  // アニメーションバリアント
  const slideInVariants = {
    hidden: { opacity: 0, y: 20, transition: { duration: 0 } },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" },
    },
    instant: { opacity: 1, y: 0, transition: { duration: 0 } },
  };

  // 遅延付きのアニメーションバリアントを生成する関数
  const getDelayedVariants = (delay: number) => {
    return {
      hidden: { opacity: 0, y: 20, transition: { duration: 0 } },
      visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.6, ease: "easeOut", delay },
      },
      instant: { opacity: 1, y: 0, transition: { duration: 0 } },
    };
  };

  return (
    <VStack spacing={0} w="100%">
      <Box
        width="100%"
        ref={storyImageRef}
        mb={{ base: "10px", sm: "10px", md: "10px", lg: "10px" }}
      >
        <motion.div
          initial="hidden"
          animate={getAnimationState(storyImageInView)}
          variants={slideInVariants}
          style={{ width: "100%" }}
        >
          <Image
            src={ScheduleImage}
            alt="schedule"
            w={{
              base: "90%", // 375px未満
              sm: "85%", // 480px未満
              md: "80%", // 768px未満
              lg: "80%", // 992px未満
              xl: "70%", // 992px以上
              "2xl": "65%", // 992px以上
            }}
            h="auto"
            maxW={{
              base: "360px",
              sm: "450px",
              md: "700px",
              lg: "900px",
              xl: "1200px",
              "2xl": "1200px",
            }}
            mx="auto"
            display="block"
            {...commonImageProps}
          />
        </motion.div>
      </Box>

      <Box
        ref={headingRef}
        width="100%"
        mb={{ base: "80px", sm: "90px", md: "100px", lg: "120px" }}
      >
        <Heading>
          <Flex direction="column" align="center" gap={4}>
            <Flex
              align="center"
              gap={{
                base: 1, // 375px未満
                sm: 2, // 480px未満
                md: 3, // 768px未満
                lg: 4, // 992px未満
              }}
              flexWrap="wrap"
              justifyContent="center"
            >
              {/* 最初の6つのテキスト要素は同じタイミングでフェードイン */}
              <motion.div
                initial="hidden"
                animate={getAnimationState(headingInView)}
                variants={slideInVariants} // 遅延なしのバリアントを使用
              >
                <Flex
                  align="center"
                  gap={{
                    base: 1,
                    sm: 2,
                    md: 3,
                    lg: 4,
                  }}
                  flexWrap="wrap"
                  justifyContent="center"
                >
                  {TEXTS.Schedule.content.slice(0, 6).map((text, index) => (
                    <Text
                      key={`schedule-content-${index}`}
                      color="rgb(215, 200, 150)"
                      whiteSpace={{
                        base: "normal",
                        md: "nowrap",
                      }}
                      fontSize={
                        index === 1 || index === 3
                          ? {
                              base: "30px",
                              sm: "36px",
                              md: "50px",
                              lg: "70px",
                              xl: "90px",
                              "2xl": "90px",
                            }
                          : {
                              base: "14px",
                              sm: "24px",
                              md: "30px",
                              lg: "40px",
                              xl: "50px",
                              "2xl": "50px",
                            }
                      }
                      ml={
                        index === 5
                          ? {
                              base: "-2",
                              sm: "-4",
                              md: "-6",
                              lg: "-8",
                              xl: "-8",
                            }
                          : undefined
                      }
                    >
                      {text}
                    </Text>
                  ))}
                </Flex>
              </motion.div>
            </Flex>
            {[
              {
                texts: [TEXTS.Schedule.content[6], TEXTS.Schedule.content[7]],
                props: {
                  color: "white",
                  mt: {
                    base: "-3", // 375px未満
                    sm: "-3", // 480px未満
                    md: "-4", // 768px未満
                    lg: "-5", // 992px未満
                    xl: "-5", // 992px以上
                  },
                  fontSize: {
                    base: "12px",
                    sm: "16px",
                    md: "20px",
                    lg: "30px",
                    xl: "35px",
                  },
                  lineHeight: {
                    base: "1.5",
                    sm: "1.6",
                    md: "1.8",
                    lg: "2.0",
                    xl: "2.0",
                  },
                },
                separator: <br />,
              },
              {
                texts: [TEXTS.Schedule.content[8]],
                props: {
                  color: "white",
                  fontSize: {
                    base: "10px",
                    sm: "14px",
                    md: "16px",
                    lg: "20px",
                  },
                },
                wrapper: (children: any) => (
                  <motion.div
                    initial="hidden"
                    animate={getAnimationState(headingInView)}
                    variants={{
                      hidden: {
                        opacity: 0,
                        y: 20,
                        transition: { duration: 0 },
                      },
                      visible: {
                        opacity: 1,
                        y: 0,
                        transition: {
                          duration: 0.6,
                          ease: "easeOut",
                          delay: 0.2,
                        },
                      },
                      instant: {
                        opacity: 1,
                        y: 0,
                        transition: { duration: 0 },
                      },
                    }}
                  >
                    <VStack>
                      <Box
                        border="1px solid white"
                        borderRadius="md"
                        px={{
                          base: 2,
                          sm: 3,
                          md: 4,
                          lg: 5,
                          xl: 5,
                        }}
                        py={{
                          base: 1,
                          sm: 1.5,
                          md: 2,
                          lg: 2,
                          xl: 2,
                        }}
                      >
                        {children}
                      </Box>
                    </VStack>
                  </motion.div>
                ),
              },
              {
                texts: [TEXTS.Schedule.content[9], TEXTS.Schedule.content[10]],
                props: [
                  {
                    color: "white",
                    whiteSpace: "nowrap",
                    fontSize: {
                      base: "10px",
                      sm: "16px",
                      md: "22px",
                      lg: "30px",
                      xl: "35px",
                      "2xl": "35px",
                    },
                  },
                  {
                    color: "white",
                    whiteSpace: "nowrap",
                    fontSize: {
                      base: "16px",
                      sm: "20px",
                      md: "30px",
                      lg: "40px",
                      xl: "50px",
                      "2xl": "50px",
                    },
                  },
                ],
                wrapper: (children: any) => (
                  <motion.div
                    initial="hidden"
                    animate={getAnimationState(headingInView)}
                    variants={{
                      hidden: {
                        opacity: 0,
                        y: 20,
                        transition: { duration: 0 },
                      },
                      visible: {
                        opacity: 1,
                        y: 0,
                        transition: {
                          duration: 0.6,
                          ease: "easeOut",
                          delay: 0.3,
                        },
                      },
                      instant: {
                        opacity: 1,
                        y: 0,
                        transition: { duration: 0 },
                      },
                    }}
                  >
                    <Flex
                      align="center"
                      gap={{
                        base: 2,
                        sm: 3,
                        md: 4,
                        lg: 5,
                      }}
                      mt={{
                        base: "12px",
                        sm: "16px",
                        md: "20px",
                        lg: "24px",
                      }}
                    >
                      {children}
                    </Flex>
                  </motion.div>
                ),
              },
              {
                texts: [TEXTS.Schedule.content[11]],
                props: {
                  color: "white",
                  fontSize: {
                    base: "10px",
                    sm: "10px",
                    md: "16px",
                    lg: "20px",
                  },
                  lineHeight: "1.5",
                  mt: {
                    base: "-10px",
                    sm: "-5px",
                    md: "6px",
                    lg: "8px",
                  },
                  mb: {
                    base: "-15px",
                    sm: "-15px",
                    md: "-10px",
                    lg: "-10px",
                  },
                },
                separator: <br />,
              },
              {
                texts: [TEXTS.Schedule.content[12]],
                props: {
                  color: "white",
                  fontSize: {
                    base: "10px",
                    sm: "10px",
                    md: "16px",
                    lg: "20px",
                  },
                },
                wrapper: (children: any) => {
                  // 新しいwrapper実装
                  return (
                    <motion.div
                      initial="hidden"
                      animate={getAnimationState(headingInView)}
                      variants={{
                        hidden: {
                          opacity: 0,
                          y: 20,
                          transition: { duration: 0 },
                        },
                        visible: {
                          opacity: 1,
                          y: 0,
                          transition: {
                            duration: 0.6,
                            ease: "easeOut",
                            delay: 0.6,
                          },
                        },
                        instant: {
                          opacity: 1,
                          y: 0,
                          transition: { duration: 0 },
                        },
                      }}
                    >
                      <Flex
                        align={{
                          base: "center",
                          md: "baseline",
                        }}
                        gap={{
                          base: 2,
                          md: 2,
                        }}
                        flexDirection={{
                          base: "column",
                          sm: "column",
                          md: "row",
                        }}
                        justifyContent={{
                          base: "center",
                          md: "center",
                        }}
                        width="100%"
                      >
                        <Box
                          as="a"
                          href="https://www.t-i-forum.co.jp"
                          target="_blank"
                          rel="noopener noreferrer"
                          color="white"
                          _hover={{
                            color: "rgb(215, 200, 150)",
                          }}
                          fontSize={{
                            base: "10px",
                            sm: "10px",
                            md: "16px",
                            lg: "20px",
                            xl: "20px",
                            "2xl": "20px",
                          }}
                          whiteSpace="nowrap"
                          display="inline-block"
                          mr={{
                            md: "0",
                            lg: "0",
                          }}
                        >
                          https://www.t-i-forum.co.jp
                        </Box>
                        <Box
                          fontSize={{
                            base: "12px",
                            sm: "14px",
                            md: "16px",
                            lg: "20px",
                          }}
                          color="white"
                          textAlign={{
                            base: "center",
                            md: "left",
                          }}
                        >
                          {children}
                        </Box>
                      </Flex>
                    </motion.div>
                  );
                },
              },
            ].map(({ texts, props, separator, wrapper }, groupIndex) => {
              // 各グループに開始遅延を設定（前のグループの6つのテキスト + 少し余裕を持たせる）
              const baseDelay =
                groupIndex === 0 ? 0.1 : 6 * 0.15 + groupIndex * 0.2;

              // groupIndex === 1, 2, 3 の場合は特別処理をスキップ (groupIndex === 4を除外)
              if (groupIndex === 1 || groupIndex === 2 || groupIndex === 3) {
                const content = texts.map((text, index) => (
                  <motion.div
                    key={`schedule-anim-group-early-${groupIndex}-${index}`}
                    initial="hidden"
                    animate={getAnimationState(headingInView)}
                    variants={{
                      hidden: {
                        opacity: 0,
                        y: 20,
                        transition: { duration: 0 },
                      },
                      visible: {
                        opacity: 1,
                        y: 0,
                        transition: {
                          duration: 0.6,
                          ease: "easeOut",
                          delay: 0.3 + (groupIndex - 1) * 0.1,
                        },
                      },
                      instant: {
                        opacity: 1,
                        y: 0,
                        transition: { duration: 0 },
                      },
                    }}
                  >
                    <Text
                      key={`schedule-text-content-${index}`}
                      {...(Array.isArray(props) ? props[index] : props)}
                    >
                      {text}
                      {separator && index < texts.length - 1 && separator}
                    </Text>
                  </motion.div>
                ));
                return wrapper ? wrapper(content) : content;
              }

              // 最後のグループ（groupIndex === 4）の場合、単一のmotion.divでコンテンツを囲む
              if (groupIndex === 4) {
                // 複雑なwrapper関数が既に定義されているので、そのままwrapperを使用
                return wrapper
                  ? wrapper(
                      texts.map((text, index) => (
                        <Text
                          key={`schedule-text-content-flat-${index}`}
                          {...(Array.isArray(props) ? props[index] : props)}
                        >
                          {text}
                          {separator && index < texts.length - 1 && separator}
                        </Text>
                      ))
                    )
                  : texts;
              }

              // その他のケース（groupIndex === 0）の場合は元の処理
              const content = texts.map((text, index) => (
                <motion.div
                  key={`schedule-anim-group-${groupIndex}-${index}`}
                  initial="hidden"
                  animate={getAnimationState(headingInView)}
                  variants={getDelayedVariants(baseDelay + index * 0.15)}
                >
                  <Text
                    key={`schedule-text-content-${index}`}
                    {...(Array.isArray(props) ? props[index] : props)}
                  >
                    {text}
                    {separator && index < texts.length - 1 && separator}
                  </Text>
                </motion.div>
              ));
              return wrapper ? wrapper(content) : content;
            })}
          </Flex>
        </Heading>
      </Box>
    </VStack>
  );
};

export default ScheduleHeader;
